import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, SearchableDropdown } from "components";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "store";
import {
  addUsersToProductLineAction,
  removeUserFromProductLineAction,
  setProductLineUsers,
} from "store/reducers/products/users/pl-users.actions";

/* --- types --- */
type RawUser = any;
type User = {
  userID: string;
  firstName: string;
  lastName: string;
  raw?: RawUser;
};

/* --- simple stable sort --- */
const collator = new Intl.Collator(undefined, { sensitivity: "base" });
const compareNames = (a = "", b = "") => {
  const aEmpty = String(a).trim().length === 0;
  const bEmpty = String(b).trim().length === 0;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  return collator.compare(a, b);
};
const compareUsers = (a: User, b: User) => {
  const last = compareNames(a.lastName, b.lastName);
  if (last !== 0) return last;
  const first = compareNames(a.firstName, b.firstName);
  if (first !== 0) return first;
  const an = Number(a.userID),
    bn = Number(b.userID);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return collator.compare(String(a.userID), String(b.userID));
};
const sortUsers = (arr: User[]) => arr.slice().sort(compareUsers);

/* --- component --- */
export default function AddUserForm({ closeMenu }: { closeMenu: () => void }) {
  const dispatch = useDispatch();
  const { users: allUsers } = useTypedSelector((s) => s.users);
  const { users: userState } = useTypedSelector((s) => s.products);
  const productlineUsers = userState?.users ?? [];

  // current IDs in productline (strings)
  const currentUsersIds = productlineUsers.map((u: any) => String(u.id ?? ""));

  // Map+sort allUsers -> User[] immediately
  const sortedAllUsers = useMemo<User[]>(() => {
    if (!allUsers?.byId) return [];
    const mapped = Object.keys(allUsers.byId).map((userID) => {
      const raw = allUsers.byId[userID];
      return {
        userID: String(userID),
        firstName: String(raw?.firstName ?? ""),
        lastName: String(raw?.lastName ?? ""),
        raw,
      } as User;
    });
    return sortUsers(mapped);
  }, [allUsers]);

  // quick lookup
  const userById = useMemo(() => {
    const m: Record<string, User> = {};
    sortedAllUsers.forEach((u) => (m[u.userID] = u));
    return m;
  }, [sortedAllUsers]);

  // Map+sort productline users -> User[]
  const sortedProductlineUsers = useMemo<User[]>(() => {
    return sortUsers(
      productlineUsers.map((pu: any) => {
        const id = String(pu.id ?? "");
        return userById[id] ?? { userID: id, firstName: "", lastName: "" };
      })
    );
  }, [productlineUsers, userById]);

  // state holds typed User[] (matches dropdown generic)
  const [selectedUsers, setSelectedUsers] = useState<User[]>(
    sortedProductlineUsers
  );
  useEffect(
    () => setSelectedUsers(sortedProductlineUsers),
    [sortedProductlineUsers]
  );

  // Callbacks for getting the label and key for user
  const getUserLabel = useCallback((u: User) => {
    const label = [u.lastName, u.firstName].filter(Boolean).join(", ");
    return label || String(u.userID);
  }, []);
  const getUserKey = useCallback((u: User) => String(u.userID), []);

  // SearchableDropdown is generic: it will call this with User[]
  const handleDropdownChange = (_keys: string[], users: User[]) => {
    // ensure typed array, dedupe if needed, and keep sorted
    setSelectedUsers(sortUsers(users));
  };

  const handleSubmitNewUsers = () => {
    if (!allUsers) return;
    const selectedIds = selectedUsers.map((u) => u.userID);

    // add missing
    for (const id of selectedIds) {
      if (currentUsersIds.includes(id)) continue;
      const raw = allUsers.byId?.[id];
      if (raw) dispatch(addUsersToProductLineAction(raw));
      else console.warn("Add: missing raw user for id", id);
    }

    // remove no-longer-selected
    for (const id of currentUsersIds) {
      if (selectedIds.includes(id)) continue;
      dispatch(removeUserFromProductLineAction(id));
    }

    // set productline users (preserve roles)
    dispatch(
      setProductLineUsers(
        selectedIds.map((id) => {
          const stateItem: any = productlineUsers.find(
            (p: any) => String(p.id) === String(id)
          );
          return { id, role: stateItem?.role ?? "VIEWER" };
        })
      )
    );

    closeMenu();
  };

  if (!allUsers?.byId) return null;

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <StyledInputWrapper>
        {/* Use the generic directly: options/value are User[] and handler receives User[] */}
        <SearchableDropdown<User>
          multiselect
          searchable
          placeholder="team members"
          options={sortedAllUsers}
          value={selectedUsers}
          handleValueChange={handleDropdownChange}
          name="searchCarrierUser"
          getOptionLabel={getUserLabel}
          getOptionKey={getUserKey}
          noShadow
          noHoverBorder
        />

        <StyledUsersActionButton
          onClick={handleSubmitNewUsers}
          buttonType="button"
          size="normal"
        >
          Update Users
        </StyledUsersActionButton>
      </StyledInputWrapper>
    </form>
  );
}

/* --- styles (copy these back into your file if you want styling) --- */
const StyledInputWrapper = styled.div<any>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;

  margin-top: 30px;
  padding-left: 32px;
  padding-right: 48px;
`;

const StyledUsersActionButton = styled(Button)`
  margin-top: 40px;
  color: white;
  white-space: nowrap;
`;
